// Compose — draft outreach with LLM-generated openers via /api/jobs cmd=contacto.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import { apiPost } from '../lib/api';
import { useSSE } from '../lib/sse';
import type { Job, OutreachAngle, OutreachTone, OutreachThread } from '../lib/types';

const STORAGE_KEY = 'nice2job.outreach';

export default function Compose() {
  const nav = useNavigate();
  const [to, setTo] = useState('');
  const [role, setRole] = useState('');
  const [angle, setAngle] = useState<OutreachAngle>('specific-work');
  const [tone, setTone] = useState<OutreachTone>('warm');
  const [context, setContext] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  const startMut = useMutation({
    mutationFn: () =>
      apiPost<{ job_id: string; status: string } | Job>('/api/jobs', {
        cmd: 'contacto',
        payload: { to, role, angle, tone, context },
      }),
    onSuccess: (job) => {
      const id = (job as { job_id?: string }).job_id || (job as Job).id;
      setJobId(id);
    },
  });

  const sse = useSSE(jobId ? `/api/jobs/${jobId}/stream` : null, { enabled: !!jobId });

  // Populate subject/body from streamed tokens as a rough draft
  const draftFromStream = sse.tokens || body;
  const [subjectDraft, bodyDraft] = splitDraft(draftFromStream, subject, body);

  const saveDraft = () => {
    const thread: OutreachThread = {
      id: `out-${Date.now()}`,
      to, role,
      status: 'Draft',
      subject: subject || subjectDraft,
      body: body || bodyDraft,
      channel: 'linkedin',
      created_at: new Date().toISOString(),
    };
    const existing: OutreachThread[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(thread);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    nav(`/outreach/${thread.id}`);
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav(-1)}>← Back</button>
      <PageHeader
        eyebrow="New message · we draft, you decide"
        title="Compose outreach."
        sub="Tell us who and why. We'll generate an opener — you keep what sounds like you."
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 28, alignItems: 'flex-start' }}>
        <div className="card" style={{ padding: 26 }}>
          <Field label="To (name)">
            <input value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} placeholder="Shauna Sweeney" />
          </Field>
          <Field label="Role @ Company">
            <input value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle} placeholder="Research Lead @ Anthropic" />
          </Field>
          <Field label="Angle">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(['specific-work', 'warm-intro', 'role-interest'] as OutreachAngle[]).map((a) => (
                <div
                  key={a}
                  onClick={() => setAngle(a)}
                  className="card"
                  style={{
                    padding: 12, cursor: 'pointer',
                    borderColor: angle === a ? 'var(--pri)' : 'var(--line)',
                    background: angle === a ? 'var(--pri-soft)' : 'var(--surface)',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {a === 'specific-work' ? 'Reference specific work' : a === 'warm-intro' ? 'Warm intro via mutual' : 'Ask about a role'}
                  </div>
                </div>
              ))}
            </div>
          </Field>
          <Field label="Tone">
            <div style={{ display: 'flex', gap: 4, background: 'var(--line2)', padding: 3, borderRadius: 8 }}>
              {(['warm', 'direct', 'playful'] as OutreachTone[]).map((t) => (
                <div
                  key={t}
                  onClick={() => setTone(t)}
                  style={{
                    flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6,
                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    background: tone === t ? 'var(--surface)' : 'transparent',
                    color: tone === t ? 'var(--ink)' : 'var(--ink3)',
                    boxShadow: tone === t ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                    textTransform: 'capitalize',
                  }}
                >
                  {t}
                </div>
              ))}
            </div>
          </Field>
          <Field label="The one thing only you could write">
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="A detail, a link, a shared moment."
            />
          </Field>
          <button
            className="btn accent lg"
            style={{ width: '100%' }}
            disabled={!to || startMut.isPending || sse.status === 'streaming'}
            onClick={() => startMut.mutate()}
          >
            {startMut.isPending || sse.status === 'streaming' ? 'Drafting…' : 'Generate opener →'}
          </button>
          {startMut.isError && (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>{(startMut.error as Error).message}</div>
          )}
        </div>

        <div>
          {!jobId ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', minHeight: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--line2)', display: 'grid', placeItems: 'center', marginBottom: 18 }}>
                <div className="serif" style={{ fontSize: 24, color: 'var(--ink3)' }}>✉</div>
              </div>
              <div className="serif" style={{ fontSize: 22, marginBottom: 6 }}>Your draft will land here.</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink3)', maxWidth: 360, lineHeight: 1.6 }}>
                Pick a recipient on the left and tell us the angle.
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 26 }}>
              <div className="tab" style={{ marginBottom: 8 }}>Subject</div>
              <input
                value={subject || subjectDraft}
                onChange={(e) => setSubject(e.target.value)}
                style={{ ...inputStyle, fontWeight: 500, marginBottom: 18 }}
              />
              <div className="tab" style={{ marginBottom: 8 }}>Body</div>
              <textarea
                value={body || bodyDraft}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                style={{ ...inputStyle, fontFamily: 'Fraunces, var(--cjk-serif), serif', resize: 'vertical', lineHeight: 1.7 }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button className="btn accent" onClick={saveDraft}>Save draft</button>
                <span style={{ fontSize: 11, color: 'var(--ink3)', alignSelf: 'center', marginLeft: 'auto' }}>
                  {(body || bodyDraft).split(/\s+/).filter(Boolean).length} words
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function splitDraft(stream: string, subjectOverride: string, bodyOverride: string): [string, string] {
  if (subjectOverride && bodyOverride) return [subjectOverride, bodyOverride];
  // Very simple heuristic: first non-blank line = subject, rest = body
  const lines = stream.split('\n');
  const firstIdx = lines.findIndex((l) => l.trim());
  const subject = firstIdx >= 0 ? lines[firstIdx].replace(/^Subject:\s*/i, '').trim() : '';
  const body = lines.slice(firstIdx + 1).join('\n').trim();
  return [subjectOverride || subject, bodyOverride || body];
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tab" style={{ marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
